/**
 * CalendarUIContext - UI State (Modals, Sidebar, Selected Events)
 * Manages ephemeral UI state that changes frequently
 * Separated to prevent unnecessary rerenders of data/action consumers
 */

import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import queryString from 'query-string';

const CalendarUIContext = createContext(undefined);

/**
 * Hook to access calendar UI state
 * @returns {object} Calendar UI state and actions
 * @throws {Error} If used outside CalendarUIProvider
 */
export const useCalendarUI = () => {
  const context = useContext(CalendarUIContext);
  if (!context) {
    throw new Error('useCalendarUI must be used within CalendarUIProvider');
  }
  return context;
};

/**
 * Calendar UI Provider
 * Provides UI state (modals, sidebar, selections) to child components
 */
export const CalendarUIProvider = ({ children }) => {
  const location = useLocation();
  const history = useHistory();

  // Initialize sidebar state based on screen size
  const getInitialSidebarState = () => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768; // Collapsed on mobile
    }
    return false;
  };

  // UI State
  const [modals, setModals] = useState({
    eventView: false,
    eventForm: false,
    settings: false,
  });

  // Day events popup state
  const [dayEventsPopup, setDayEventsPopup] = useState({
    visible: false,
    date: null,
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialSidebarState);

  // CHANGED: Only store event ID and metadata, not full event object
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEventMetadata, setSelectedEventMetadata] = useState(null); // For recurring events

  const [selectedEvent, setSelectedEvent] = useState(null); // For form modal (still needs full object)
  const [eventFormMode, setEventFormMode] = useState('create'); // 'create' or 'edit'

  // Form-related state
  const [locationsList, setLocationsList] = useState([]);
  const [officeLocationsList, setOfficeLocationsList] = useState([]);

  // Modal Actions - STABLE
  const openModal = useCallback((modalName) => {
    setModals((prev) => ({ ...prev, [modalName]: true }));
  }, []);

  const closeModal = useCallback((modalName) => {
    setModals((prev) => ({ ...prev, [modalName]: false }));
  }, []);

  /**
   * Open event view modal - ONLY accepts event/task ID
   * Modal will fetch full event/task data from API
   * @param {string|object} eventIdOrEvent - Event/Task ID string or event/task object (extracts ID)
   */
  const openEventViewModal = useCallback((eventIdOrEvent) => {
    // Support both ID string and event/task object for backward compatibility
    // But extract only the ID and minimal metadata
    if (typeof eventIdOrEvent === 'string') {
      setSelectedEventId(eventIdOrEvent);
      setSelectedEventMetadata(null);
    } else if (eventIdOrEvent) {
      // Handle both events (with 'id') and tasks (with 'task_id')
      const extractedId = eventIdOrEvent.extendedProps?.sk;

      if (!extractedId) {
        console.warn('Attempted to open EventViewModal without a valid ID or task_id:', eventIdOrEvent);
        return;
      }

      setSelectedEventId(extractedId);
      // Extract minimal metadata needed for API call (for recurring events)
      setSelectedEventMetadata({
        sk: eventIdOrEvent.extendedProps?.sk || eventIdOrEvent.sk,
        pk: eventIdOrEvent.extendedProps?.pk || eventIdOrEvent.pk,
        is_recurring:
          eventIdOrEvent.extendedProps?.isRecurring || eventIdOrEvent.extendedProps?.is_recurring || eventIdOrEvent.is_recurring,
        meeting_start_time: eventIdOrEvent.start || eventIdOrEvent.task_start_date,
        meeting_end_time: eventIdOrEvent.end || eventIdOrEvent.task_due_date,
        is_clone: eventIdOrEvent.extendedProps?.is_clone || eventIdOrEvent.is_clone,
      });
    }
    setModals((prev) => ({ ...prev, eventView: true }));
  }, []);

  const openEventFormModal = useCallback((mode = 'create', event = null) => {
    setEventFormMode(mode);
    setSelectedEvent(event);
    setModals((prev) => ({ ...prev, eventForm: true }));
  }, []);

  const closeEventViewModal = useCallback(() => {
    setModals((prev) => ({ ...prev, eventView: false }));
    setSelectedEventId(null);
    setSelectedEventMetadata(null);
  }, []);

  const closeEventFormModal = useCallback(() => {
    // Clear createEvent URL parameter if present
    const queryParams = queryString.parse(location.search);
    if (queryParams.createEvent) {
      const newParams = { ...queryParams };
      delete newParams.createEvent;
      const newSearch = queryString.stringify(newParams);
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });
    }
    setModals((prev) => ({ ...prev, eventForm: false }));
    setSelectedEvent(null);
    setEventFormMode('create');
  }, [location.search, location.pathname, history]);

  const openSettingsModal = useCallback(() => {
    setModals((prev) => ({ ...prev, settings: true }));
  }, []);

  const closeSettingsModal = useCallback(() => {
    setModals((prev) => ({ ...prev, settings: false }));
  }, []);

  // Sidebar Actions - STABLE
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => !prev);
  }, []);

  // Day Events Popup Actions
  const openDayEventsPopup = useCallback((date) => {
    setDayEventsPopup({
      visible: true,
      date,
    });
  }, []);

  const closeDayEventsPopup = useCallback(() => {
    setDayEventsPopup({
      visible: false,
      date: null,
    });
  }, []);

  // Handle URL-based create event - open modal if URL has createEvent=true
  useEffect(() => {
    const queryParams = queryString.parse(location.search);
    const createEventFromUrl = queryParams.createEvent === 'true';

    if (createEventFromUrl && !modals.eventForm) {
      setEventFormMode('create');
      setSelectedEvent(null);
      setModals((prev) => ({ ...prev, eventForm: true }));
    }
  }, [location.search, modals.eventForm]);

  // Event Selection Actions - STABLE
  const selectEvent = useCallback((event) => {
    setSelectedEvent(event);
  }, []);

  const clearSelectedEvent = useCallback(() => {
    setSelectedEvent(null);
    setSelectedEventId(null);
    setSelectedEventMetadata(null);
  }, []);

  // Memoize UI state and actions
  const value = useMemo(
    () => ({
      // State
      modals,
      sidebarCollapsed,
      selectedEvent, // For form modal
      selectedEventId, // NEW: Only ID for view modal
      selectedEventMetadata, // NEW: Minimal metadata for API calls
      eventFormMode,
      dayEventsPopup, // Day events popup state

      // Form-related state
      locationsList,
      setLocationsList,
      officeLocationsList,
      setOfficeLocationsList,

      // Actions
      openModal,
      closeModal,
      openEventViewModal,
      openEventFormModal,
      closeEventViewModal,
      closeEventFormModal,
      openSettingsModal,
      closeSettingsModal,
      toggleSidebar,
      selectEvent,
      clearSelectedEvent,
      openDayEventsPopup,
      closeDayEventsPopup,
    }),
    [
      modals,
      sidebarCollapsed,
      selectedEvent,
      selectedEventId,
      selectedEventMetadata,
      eventFormMode,
      dayEventsPopup,
      locationsList,
      officeLocationsList,
      openModal,
      closeModal,
      openEventViewModal,
      openEventFormModal,
      closeEventViewModal,
      closeEventFormModal,
      openSettingsModal,
      closeSettingsModal,
      toggleSidebar,
      selectEvent,
      clearSelectedEvent,
      openDayEventsPopup,
      closeDayEventsPopup,
    ]
  );

  return <CalendarUIContext.Provider value={value}>{children}</CalendarUIContext.Provider>;
};

export default CalendarUIContext;
